import React, { useState, useEffect } from 'react';
import { useLinkedIn } from '../../context/LinkedInContext';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const OutreachQueue = () => {
  const { tasks, prospects, completeTask, skipTask, stats, generateAiIcebreaker } = useLinkedIn();
  const [activeTaskIndex, setActiveTaskIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [customMessage, setCustomMessage] = useState('');

  // Filter tasks to show only open ones due today or before
  const queue = tasks.filter(t => t.status === 'Åpen').sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  const currentTask = queue[activeTaskIndex];
  const currentProspect = prospects.find(p => p.id === currentTask?.prospect_id);
  const isResearchTask = currentTask?.type === 'Research';
  const prospectDisplayName = currentProspect?.full_name || currentProspect?.company_name || 'Prospekt';

  useEffect(() => {
    if (currentTask) {
      // Replace variables in template
      let msg = currentTask.template_text || '';
      if (currentProspect && !isResearchTask) {
        const firstName = (currentProspect.full_name || currentProspect.company_name || 'Prospekt').split(' ')[0];
        msg = msg.replace('{{FirstName}}', firstName);
        msg = msg.replace('{{Company}}', currentProspect.company_name);
      }
      setCustomMessage(msg);
    }
  }, [currentTask, currentProspect, isResearchTask]);

  const handleCopy = async () => {
    const textToCopy = isResearchTask
      ? currentProspect?.linkedin_url || ''
      : customMessage;

    setCopyError('');

    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error('Clipboard API unavailable');
      }

      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
      setCopyError('Kunne ikke kopiere automatisk. Marker teksten eller lenken og kopier den manuelt.');
    }
  };

  const handleOpenLinkedIn = () => {
    if (currentProspect?.linkedin_url) {
      window.open(currentProspect.linkedin_url, '_blank');
    }
  };

  const handleAiImprove = async () => {
    setAiLoading(true);
    const newText = await generateAiIcebreaker(currentProspect);
    setCustomMessage(newText);
    setAiLoading(false);
  };

  if (queue.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px] text-center bg-white rounded-[2rem] border border-slate-200">
        <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center mb-6">
          <SafeIcon icon="✅" className="w-12 h-12 text-emerald-500" />
        </div>
        <h2 className="text-2xl font-black text-slate-900">Alt utført!</h2>
        <p className="text-slate-500 font-medium max-w-xs mx-auto mt-2">
          Ingen flere outreach-oppgaver for i dag. Godt jobbet.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-[calc(100vh-200px)]">
      {/* LEFT: Task List */}
      <div className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
          <h3 className="font-black text-slate-900">Kø ({queue.length})</h3>
          <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${stats.todayCount >= stats.dailyLimit ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
            {stats.todayCount} / {stats.dailyLimit} i dag
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar">
          {queue.map((task, idx) => {
            const p = prospects.find(pros => pros.id === task.prospect_id);
            const isActive = idx === activeTaskIndex;
            const isResearch = task.type === 'Research';
            return (
              <div 
                key={task.id} 
                onClick={() => setActiveTaskIndex(idx)}
                className={`p-4 rounded-xl cursor-pointer transition-all border-2 ${isActive ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-transparent hover:bg-slate-50'}`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-[10px] font-black uppercase tracking-widest ${isActive ? 'text-slate-400' : 'text-slate-400'}`}>{isResearch ? 'Research' : task.type}</span>
                  <span className="text-xs font-bold opacity-70">{task.due_date}</span>
                </div>
                {isResearch ? (
                  <p className="font-bold text-sm truncate">Finn kontaktperson</p>
                ) : (
                  <p className="font-bold text-sm truncate">{p?.full_name || 'Ukjent'}</p>
                )}
                <p className={`text-xs ${isActive ? 'text-slate-400' : 'text-slate-500'}`}>{p?.company_name}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER & RIGHT: Workflow Area */}
      <div className="lg:col-span-2 flex flex-col gap-6">
        {/* Prospect Info Card */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-lg shadow-blue-200">
              {prospectDisplayName.substring(0, 2).toUpperCase()}
            </div>
            <div>
              {isResearchTask ? (
                <>
                  <h2 className="text-2xl font-black text-slate-900">Finn kontaktperson på LinkedIn</h2>
                  <p className="text-slate-500 font-medium">{currentProspect?.company_name}</p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-black text-slate-900">{currentProspect?.full_name}</h2>
                  <p className="text-slate-500 font-medium">{currentProspect?.title} @ {currentProspect?.company_name}</p>
                </>
              )}
            </div>
          </div>
          <button 
            onClick={handleOpenLinkedIn}
            className="flex items-center gap-2 px-6 py-3 bg-[#0077b5] text-white rounded-xl font-bold hover:bg-[#006396] transition-all shadow-lg"
          >
            <SafeIcon icon="🔗" /> {isResearchTask ? 'Åpne LinkedIn-søk' : 'Åpne LinkedIn'}
          </button>
        </div>

        {/* Action Card */}
        <div className="flex-1 bg-slate-900 text-white rounded-[2.5rem] p-8 relative overflow-hidden shadow-2xl flex flex-col">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
          
          {isResearchTask ? (
            <>
              <div className="relative z-10 flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/10 rounded-xl border border-white/20">
                    <SafeIcon icon="🔎" className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-black text-xl">Finn kontaktperson på LinkedIn</h3>
                    <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Manuell researchoppgave</p>
                  </div>
                </div>
              </div>

              <div className="flex-1 relative mb-6 space-y-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <p className="text-sm font-bold text-slate-300 mb-2">Selskap</p>
                  <p className="text-lg font-semibold">{currentProspect?.company_name || 'Ukjent selskap'}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <p className="text-sm font-bold text-slate-300 mb-2">Notat</p>
                  <p className="text-lg font-medium text-white">
                    {currentTask.template_text || currentProspect?.notes || 'Finn relevant kontaktperson på LinkedIn'}
                  </p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <p className="text-sm font-bold text-slate-300 mb-2">LinkedIn-søk</p>
                  <p className="text-sm text-slate-300 break-all">{currentProspect?.linkedin_url || 'Ingen LinkedIn-søk tilgjengelig'}</p>
                </div>
                <button 
                  onClick={handleCopy}
                  disabled={!currentProspect?.linkedin_url}
                  className="absolute bottom-4 right-4 px-4 py-2 bg-white text-slate-900 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
                >
                  <SafeIcon icon={copied ? "✅" : "📋"} /> {copied ? 'Kopiert!' : 'Kopier LinkedIn-søk'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="relative z-10 flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/10 rounded-xl border border-white/20">
                    <SafeIcon icon={currentTask.type === 'Connect' ? "👋" : "💬"} className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-black text-xl">Steg: {currentTask.type}</h3>
                    <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Kopier tekst, utfør handlingen manuelt på LinkedIn, og marker deretter oppgaven som utført.</p>
                  </div>
                </div>
                <button 
                  onClick={handleAiImprove} 
                  disabled={aiLoading}
                  className="px-4 py-2 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/30 rounded-lg text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-all"
                >
                  {aiLoading ? <SafeIcon icon="🔄" className="animate-spin" /> : <SafeIcon icon="✨" />}
                  AI Forbedring
                </button>
              </div>

              <div className="flex-1 relative mb-6">
                <textarea 
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  className="w-full h-full bg-white/5 border border-white/10 rounded-2xl p-6 text-lg font-medium text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 resize-none"
                />
                <button 
                  onClick={handleCopy}
                  className="absolute bottom-4 right-4 px-4 py-2 bg-white text-slate-900 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 transition-all flex items-center gap-2 shadow-lg"
                >
                  <SafeIcon icon={copied ? "✅" : "📋"} /> {copied ? 'Kopiert!' : 'Kopier tekst'}
                </button>
              </div>
            </>
          )}

          {copyError && (
            <p className="relative z-10 mb-4 text-sm font-medium text-amber-300">
              {copyError}
            </p>
          )}

          <div className="flex gap-4 relative z-10">
            <button 
              onClick={() => skipTask(currentTask.id)}
              className="px-6 py-4 bg-slate-800 text-slate-400 rounded-2xl font-bold text-sm hover:bg-slate-700 transition-all"
            >
              Hopp over
            </button>
            <button 
              onClick={() => completeTask(currentTask.id)}
              className="flex-1 px-6 py-4 bg-emerald-500 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2"
            >
              <SafeIcon icon="✓" className="w-5 h-5" /> Marker utført
            </button>
          </div>
        </div>

        <p className="text-center text-xs font-medium text-slate-500">
          LinkedIn Outreach er manuell. Systemet åpner/kopierer LinkedIn-lenker og tekstforslag, men scraper ikke LinkedIn og sender ikke meldinger automatisk.
        </p>
      </div>
    </div>
  );
};

export default OutreachQueue;